import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { assetService } from "../../../../shared/services/AssetService";
import { mathCalculationService } from "../../../../shared/services/MathCalculationService";
import {
    useAppSidePanelContextProvider
} from "../../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { ChangingDirectionType } from "../../types";
import { SaveCustomImpliedVolatilityButton } from "../SaveCustomImpliedVolatilityButton";
import { StepChangeSelector } from "../StepChangeSelector";
import { PositionImpliedVolatilityChangingParameters } from "./PositionImpliedVolatilityChangingParameters";

const defaultStepValue = 1;

export function PositionImpliedVolatilitySelector({
    parameters,
}: {
    parameters: PositionImpliedVolatilityChangingParameters;
}) {
    const {
        selectedPortfolio,
    } = useAppSidePanelContextProvider();

    const isCustomImpliedVolatilityEnabled = selectedPortfolio !== null && selectedPortfolio.portfolioSettings !== null
        ? selectedPortfolio.portfolioSettings.isCustomImpliedVolatilityEnabled
        : false;

    const [stepValue, setStepValue] = useState<number>(
        parameters.selectedPosition?.customImpliedVolatilityStep ?? defaultStepValue
    );

    useEffect(() => {
        setStepValue(parameters.selectedPosition?.customImpliedVolatilityStep ?? defaultStepValue);
    }, [parameters.selectedPosition]);

    const isApplicable =
        isCustomImpliedVolatilityEnabled &&
        parameters.selectedPosition !== null &&
        assetService.isOptionType(parameters.selectedPosition.type) &&
        parameters.selectedPosition.currentSize !== 0 &&
        parameters.selectedPosition.expirationPrice === null;

    const handleChange = async (direction: ChangingDirectionType, step: number): Promise<void> => {
        const selectedPosition = parameters.selectedPosition;

        if (selectedPosition === null) {
            return;
        }

        const position = parameters.positions.find(x => x.symbol === selectedPosition.symbol);

        if (position === undefined || position.customImpliedVolatility == null)
            return;

        switch (direction) {
            case ChangingDirectionType.Next:
                position.customImpliedVolatility = mathCalculationService.round(position.customImpliedVolatility + step / 100, 4);
                break;

            case ChangingDirectionType.Previous:
                position.customImpliedVolatility = mathCalculationService.round(position.customImpliedVolatility - step / 100, 4);
                break;
        }

        await parameters.updatePositionInSelectedPortfolioWrapper(position.symbol, position.customImpliedVolatility, step);
    }

    const handleStepSelectionChange = async (step: number): Promise<void> => {
        const selectedPosition = parameters.selectedPosition;

        if (selectedPosition === null || selectedPosition.customImpliedVolatility == null) {
            return;
        }

        await parameters.updatePositionInSelectedPortfolioWrapper(
            selectedPosition.symbol,
            selectedPosition.customImpliedVolatility,
            step
        );
    }

    return (
        <Stack direction="row" spacing={0.5}>
            <StepChangeSelector
                selectedStep={stepValue}
                setSelectedStep={setStepValue}
                stepOptions={[10, 5, 1, 0.5, 0.1, 0.05, 0.01]}
                disabled={!isApplicable}
                buttonContent="V"
                previousTooltipText="Reduce implied volatility"
                nextTooltipText="Increase implied volatility"
                onChange={handleChange}
                onStepSelectionChange={handleStepSelectionChange}
                selectorWidth={65}
            />
            <SaveCustomImpliedVolatilityButton />
        </Stack>
    );
}
